class CartUpsellAddButton extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('.cart-upsell-add__btn');
    this.errorEl = this.querySelector('.cart-upsell-add__error');
    if (!this.btn) return;

    this.clickHandler = this.handleClick.bind(this);
    this.btn.addEventListener('click', this.clickHandler);
  }

  disconnectedCallback() {
    if (this.btn && this.clickHandler) {
      this.btn.removeEventListener('click', this.clickHandler);
    }
  }

  async handleClick(evt) {
    evt.preventDefault();

    const variantId = this.dataset.variantId;
    if (!variantId || this.btn.getAttribute('aria-disabled') === 'true') return;

    this.setError(null);
    this.btn.setAttribute('aria-disabled', 'true');
    this.btn.classList.add('is-loading');

    // Ask Shopify to re-render the header and cart drawer along with the add,
    // so the drawer contents and the cart count stay in sync in one round trip.
    const sectionsToUpdate = ['page-header', 'cart-drawer']
      .map((sel) => document.querySelector(sel))
      .filter((el) => el);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('sections_url', window.location.pathname);
    formData.append('sections', sectionsToUpdate.map((el) => el.dataset.sectionId).join(','));

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', ')).join(', ');
        }
        throw new Error(error || response.status);
      }

      this.btn.classList.remove('is-loading');
      this.btn.classList.add('is-success');

      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId: data.variant_id }
      }));

      // Refreshing the drawer replaces this element, so no need to reset state.
      sectionsToUpdate.forEach((el) => {
        if (data.sections && data.sections[el.dataset.sectionId]) {
          el.updateFromCartChange(data.sections[el.dataset.sectionId]);
        }
      });
    } catch (error) {
      this.setError(typeof error.message === 'string' ? error.message : `${error}`);
      this.btn.classList.remove('is-loading');
      this.btn.removeAttribute('aria-disabled');
    }
  }

  setError(message) {
    if (!this.errorEl) return;
    if (message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    } else {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }
}

window.customElements.define('cart-upsell-add-button', CartUpsellAddButton);

/**
 * Builds add to cart buttons for cross-sell products that the cart drawer
 * fetches and injects client-side (the automatic recommendations path).
 *
 * The markup mirrors snippets/cart-upsell-add-button.liquid.
 */
theme.buildCartUpsellAddButtons = (container) => {
  const config = window.cartUpsellAdd;
  if (!config || !config.enabled || !container) return;

  container.querySelectorAll('.js-upsell-product-data').forEach((dataEl) => {
    const block = dataEl.previousElementSibling;
    if (!block || !block.matches('product-block')) return;
    if (block.querySelector('.cart-upsell-add')) return;

    let product;
    try {
      product = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'cart-upsell-add';

    if (!product.available || !product.variantAvailable || !product.variantId) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn cart-upsell-add__btn';
      btn.disabled = true;
      btn.textContent = config.soldOutLabel;
      wrapper.appendChild(btn);
    } else {
      const el = document.createElement('cart-upsell-add-button');
      el.dataset.variantId = product.variantId;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn cart-upsell-add__btn';

      const label = document.createElement('span');
      label.className = 'cart-upsell-add__label';
      label.textContent = config.addLabel;
      btn.appendChild(label);

      const spinner = document.createElement('span');
      spinner.className = 'cart-upsell-add__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      btn.appendChild(spinner);

      el.appendChild(btn);

      const error = document.createElement('div');
      error.className = 'cart-upsell-add__error';
      error.setAttribute('role', 'alert');
      error.hidden = true;
      el.appendChild(error);

      wrapper.appendChild(el);
    }

    block.appendChild(wrapper);
    dataEl.remove();
  });
};
